import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Garage } from "./Garage";
import { commonButtonProps } from "../../../utils/styles";
import { commonTextFieldProps } from "../../../utils/styles";

interface AddGarageModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (garage: any) => void;
  garage?: Garage;
  isEdit?: boolean;
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters"),
  contactName: Yup.string()
    .required("Contact person name is required")
    .min(2, "Contact person name must be at least 2 characters")
    .max(50, "Contact person name must not exceed 50 characters"),
  contactNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^[0-9]+$/, "Contact number must contain only digits")
    .min(10, "Contact number must be exactly 10 digits")
    .max(10, "Contact number must be exactly 10 digits"),
  location: Yup.string()
    .required("Location is required")
    .max(100, "Location must not exceed 100 characters"),
});

const initialValues: Omit<Garage, "id"> = {
  name: "",
  contactName: "",
  contactNumber: "",
  location: "",
  companyId: "",
};

const AddGarageModal: React.FC<AddGarageModalProps> = ({
  open,
  onClose,
  onSubmit,
  garage,
  isEdit = false,
}) => {
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;

  const handleSubmit = (values: any) => {
    const enrichedValues = {
      ...values,
      ...(!isEdit && { companyId: companyId }),
    };
    onSubmit(enrichedValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Update Garage" : "Add New Garage"}</DialogTitle>
      <Formik
        initialValues={garage || initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isValid, dirty }) => (
          <Form>
            <DialogContent sx={{ p: 2, pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Garage Name"
                    name="name"
                    value={values.name}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Location"
                    name="location"
                    error={touched.location && Boolean(errors.location)}
                    helperText={touched.location && errors.location}
                    placeholder="Enter location"
                    value={values.location}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Contact Person Name"
                    name="contactName"
                    value={values.contactName}
                    error={touched.contactName && Boolean(errors.contactName)}
                    helperText={touched.contactName && errors.contactName}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Contact Number"
                    name="contactNumber"
                    value={values.contactNumber}
                    error={
                      touched.contactNumber && Boolean(errors.contactNumber)
                    }
                    helperText={touched.contactNumber && errors.contactNumber}
                    inputProps={{
                      maxLength: 10,
                    }}
                    InputProps={{
                      inputMode: "numeric",
                    }}
                    placeholder="Enter 10 digit number"
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} {...commonButtonProps}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!(isValid && dirty)}
                {...commonButtonProps}
              >
                {isEdit ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddGarageModal;
